import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { DownloadCsv } from '@/components/admin/download-csv';
import { Pager } from '@/components/pager';
import { Callout, EmptyState, buttonClass, inputClass } from '@/components/ui';
import { formatCents } from '@/lib/money';
import { classWord } from '@/lib/stock-class';
import { NO_COST_YET, dayWords, type StockValueTotal } from '@/lib/stock-value';
import { stockValueQuery } from '@/queries';

/**
 * Reports, Stock value. Spec 017, FR-1701 to FR-1713.
 *
 * Guy, 29 October 2026: the value of the stock at the end of each month, at
 * the true value of that day, so two months can be compared. The default is
 * today. A past day is worked out from the stock movements and the cost
 * history, and a closed month shows the copy kept on the first of the next
 * month, which a later change does not move.
 *
 * Value is on hand times the average cost, so reserved stock is in it. An
 * item with no cost says "No cost yet", and the totals say how many units the
 * value leaves out. The accountant posts the month-end figure by hand: nothing
 * here goes to Xero (FR-1725).
 */

const PAGE = 100;

function units(n: number): string {
  return n.toLocaleString('en-AU', { maximumFractionDigits: 3 });
}

function Totals({ title, rows, showCost }: { title: string; rows: StockValueTotal[]; showCost: boolean }) {
  return (
    <section className="min-w-0">
      <h2 className="mb-2 text-[13px] font-bold uppercase tracking-[0.08em] text-muted">{title}</h2>
      <div className="overflow-x-auto rounded-[6px] border border-line bg-panel">
        <table className="dt w-full text-left">
          <thead>
            <tr>
              <th scope="col">{title === 'By class' ? 'Class' : 'Location'}</th>
              <th scope="col" className="text-right">Lines</th>
              <th scope="col" className="text-right">On hand</th>
              {showCost && <th scope="col" className="text-right">Value</th>}
              {showCost && <th scope="col" className="text-right">No cost yet</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.name}>
                <td className="text-[13px]">{t.name}</td>
                <td className="text-right tabular-nums text-[13px]">{t.lines}</td>
                <td className="text-right tabular-nums text-[13px]">{units(t.on_hand)}</td>
                {showCost && <td className="text-right tabular-nums text-[13px]">{formatCents(t.value_cents ?? 0)}</td>}
                {showCost && (
                  <td className="text-right tabular-nums text-[13px] text-muted">
                    {(t.no_cost_units ?? 0) > 0 ? `${units(t.no_cost_units!)} units` : ''}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function AdminStockValuePage() {
  // Empty is today. The date box holds what was typed until it is a whole day.
  const [asAt, setAsAt] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const { data, error, isFetching } = useQuery({
    ...stockValueQuery(asAt),
    placeholderData: keepPreviousData,
    retry: false,
  });

  const rows = data?.rows ?? [];
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const shown = rows.slice((page - 1) * PAGE, page * PAGE);
  const showCost = data?.show_cost ?? false;
  const grand = data?.totals.grand;

  return (
    <div>
      <div className="mb-[14px] flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Stock value</h1>
          <p className="mt-1 max-w-[70ch] text-[12.5px] text-muted">
            The stock on hand and its value at one day. Value is on hand times the average cost,
            so reserved stock is in it.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="block">
            <span className="mb-1 block text-[12.5px] font-semibold">As at</span>
            <input
              type="date"
              className={inputClass}
              value={asAt ?? data?.as_at ?? ''}
              min={data?.history_starts ?? undefined}
              onChange={(e) => { setAsAt(e.target.value || null); setPage(1); }}
            />
          </label>
          <button
            type="button"
            className={buttonClass('secondary', 'sm')}
            disabled={asAt === null}
            onClick={() => { setAsAt(null); setPage(1); }}
          >
            Today
          </button>
          {showCost && <DownloadCsv path="/api/reports/stock-value.csv" params={{ as_at: asAt ?? undefined }} />}
        </div>
      </div>

      {error && (
        <Callout tone="warn" title="That day cannot be shown" className="mb-5">
          {(error as Error).message}
        </Callout>
      )}

      {data && !error && (
        <p className="mb-4 text-[13px] text-muted">
          {data.today
            ? 'Today, from the stock on hand now.'
            : data.closed_at
              ? `As at ${dayWords(data.as_at)}. Closed on ${new Date(data.closed_at).toLocaleDateString('en-AU')}. `
                + 'A later change does not change these figures.'
              : `As at ${dayWords(data.as_at)}, worked out from the stock movements and the cost history. `
                + 'Reserved and free are not kept for a past day.'}
          {data.history_starts && ` Cost history starts on ${dayWords(data.history_starts)}.`}
        </p>
      )}

      {data && !showCost && (
        <Callout tone="neutral" title="Costs are not shown" className="mb-5">
          You do not have the costs.view permission, so the average cost and the value are not on
          this screen, and the file cannot be downloaded.
        </Callout>
      )}

      {grand && (
        <div className="mb-6 grid gap-px overflow-hidden rounded-[10px] border border-line bg-line sm:grid-cols-3">
          {showCost && (
            <div className="bg-panel px-4 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Value</p>
              <p className="mt-1 text-[20px] font-semibold tabular-nums">{formatCents(grand.value_cents ?? 0)}</p>
              <p className="mt-0.5 text-[12px] text-muted">
                {(grand.no_cost_units ?? 0) > 0
                  ? `Leaves out ${units(grand.no_cost_units!)} units with no cost yet`
                  : 'Every unit has a cost'}
              </p>
            </div>
          )}
          <div className="bg-panel px-4 py-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">On hand</p>
            <p className="mt-1 text-[20px] font-semibold tabular-nums">{units(grand.on_hand)}</p>
            <p className="mt-0.5 text-[12px] text-muted">Units, all locations</p>
          </div>
          <div className="bg-panel px-4 py-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Lines</p>
            <p className="mt-1 text-[20px] font-semibold tabular-nums">{grand.lines}</p>
            <p className="mt-0.5 text-[12px] text-muted">One item at one location</p>
          </div>
        </div>
      )}

      {data && (
        <div className="mb-8 grid gap-6 lg:grid-cols-2">
          <Totals title="By class" rows={data.totals.by_class} showCost={showCost} />
          <Totals title="By location" rows={data.totals.by_location} showCost={showCost} />
        </div>
      )}

      {data && rows.length === 0 && (
        <EmptyState title="No stock at this day" body="No item had stock on hand at this day." />
      )}

      {rows.length > 0 && (
        <section>
          <h2 className="mb-2 text-[13px] font-bold uppercase tracking-[0.08em] text-muted">Lines</h2>
          <div className="overflow-hidden rounded-[6px] border border-line bg-panel">
            <div className="overflow-x-auto">
              <table className="dt w-full min-w-[900px] text-left">
                <thead>
                  <tr>
                    <th scope="col">Code</th>
                    <th scope="col">Product</th>
                    <th scope="col">Colour</th>
                    <th scope="col">Size</th>
                    <th scope="col">Class</th>
                    <th scope="col">Location</th>
                    <th scope="col" className="text-right">On hand</th>
                    <th scope="col" className="text-right">Reserved</th>
                    <th scope="col" className="text-right">Free</th>
                    {showCost && <th scope="col" className="text-right">Average cost</th>}
                    {showCost && <th scope="col" className="text-right">Value</th>}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => (
                    <tr key={`${r.item_id}-${r.location_id}`}>
                      <td className="font-[family-name:var(--font-mono)] text-[12.5px]">{r.code}</td>
                      <td className="text-[13px]">{r.product}</td>
                      <td className="text-[13px]">{r.colour}</td>
                      <td className="text-[13px]">{r.size}</td>
                      <td className="text-[13px]">{classWord(r.class)}</td>
                      <td className="font-[family-name:var(--font-mono)] text-[12.5px]">{r.location_code}</td>
                      <td className="text-right tabular-nums text-[13px]">{units(r.on_hand)}</td>
                      <td className="text-right tabular-nums text-[13px] text-muted">{r.reserved == null ? '' : units(r.reserved)}</td>
                      <td className="text-right tabular-nums text-[13px] text-muted">{r.free == null ? '' : units(r.free)}</td>
                      {showCost && (
                        <td className="text-right tabular-nums text-[13px]">
                          {r.avg_cost_cents == null ? <span className="text-muted">{NO_COST_YET}</span> : formatCents(r.avg_cost_cents)}
                        </td>
                      )}
                      {showCost && (
                        <td className="text-right tabular-nums text-[13px]">
                          {r.value_cents == null ? '' : formatCents(r.value_cents)}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager
              page={page}
              pages={pages}
              total={rows.length}
              limit={PAGE}
              noun="line"
              busy={isFetching}
              onPage={(n) => { setPage(n); window.scrollTo({ top: 0 }); }}
            />
          </div>
        </section>
      )}
    </div>
  );
}
